import React from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  frequencyOptions
} from '../Subscriptions/helpers';
import { useStyles } from '../Subscriptions/styles';


const EditMembershipModal = ({
  editableMembership,
  setEditableMembership,
  handleSaveMembershipOption
}) => {
  const classes = useStyles();

  const { editableQuoteProduct, quoteProductSubscriptionOptions } = useSelector((state: IReduxState) => state.quote);
  const sortOrdersArray = quoteProductSubscriptionOptions?.map((option) => option.ssort_order);
  const frequencyTitle = `${editableMembership?.frequency_count} ${editableMembership?.frequency_unit}${editableMembership?.frequency_count > 1 ? 's' : ''}`;

  const handleFrequencyChange = (event, setFieldValue, handleChange) => {
    const { value } = event.target;
    const [frequencyCount, frequencyUnit] = value.split(/\s+/);

    setFieldValue('editedMembershipOption.frequency_unit', frequencyUnit.replace('s', ''));
    setFieldValue('editedMembershipOption.frequency_count', frequencyCount);
    handleChange(event);
  };

  const handlePriceChange = (fieldName, floatValue, setFieldValue, values) => {
    const { installment_price, first_period_price, adjustment_price } = values.editedMembershipOption;
    let newInstallmentPrice = installment_price;
    let newFirstPeriodPrice = first_period_price;
    let newAdjustmentPrice = adjustment_price;

    if (fieldName === 'first_period_price') {
      newFirstPeriodPrice = floatValue;
      newAdjustmentPrice = newFirstPeriodPrice - installment_price;
    } else if (fieldName === 'installment_price') {
      newInstallmentPrice = floatValue;
      newAdjustmentPrice = first_period_price - newInstallmentPrice;
    }

    setFieldValue('editedMembershipOption.first_period_price', newFirstPeriodPrice);
    setFieldValue('editedMembershipOption.installment_price', newInstallmentPrice);
    setFieldValue('editedMembershipOption.adjustment_price', newAdjustmentPrice);
  };
  return (
    <Dialog
      className={classes.dialog}
      open={Boolean(editableMembership)}
      onClose={() => setEditableMembership(null)}
      aria-labelledby="edit-subscription-option"
    >
      <DialogTitle id="edit-subscription-option">Membership Subscriptions Options:</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Edit options for membership subscription
        </DialogContentText>
        <Formik
          initialValues={{
            editedMembershipOption: {
              id: editableMembership?.id,
              product_id: editableQuoteProduct?.id,
              first_period_price: editableMembership?.adjustment_price + editableMembership?.installment_price,
              adjustment_price: editableMembership?.adjustment_price,
              frequency_count: editableMembership?.frequency_count,
              frequency_unit: editableMembership?.frequency_unit,
              sproduct_label: editableMembership?.sproduct_label || '',
              installment_price: editableMembership?.installment_price,
              additional_information: editableMembership?.additional_information,
              ssort_order: editableMembership?.ssort_order,
            },
            frequency: editableMembership
              ? frequencyTitle
              : '',
            percent_discount: editableMembership
              ? `${editableMembership.additional_information.length
                ? JSON.parse(`${editableMembership.additional_information[0]}`).discount_percentage
                : ''}${editableMembership.frequency_count != 0 ? '%' : ''}`
              : '',
          }}
          enableReinitialize
          validationSchema={
            yup.object({
              frequency: yup
                .string()
                .required('Required'),
              editedMembershipOption: yup.object({
                first_period_price: yup
                  .number().typeError('Allowed only numerical symbols')
                  .positive('Value must be positive')
                  .required('Field is required'),
                installment_price: yup
                  .number().typeError('Allowed only numerical symbols')
                  .positive('Value must be positive')
                  .required('Field is required'),
                sproduct_label: yup
                  .string()
                  .required('Required'),
                ssort_order: yup
                  .number().typeError('Allowed only numerical symbols')
                  .positive('Value must be positive')
                  .required('Field is required')
                  .test('ssort_order', 'Sort Order is already exists', (value) => {
                    if (editableMembership?.ssort_order === value) {
                      return true;
                    }

                    return !sortOrdersArray.includes(value);
                  }),
              }),
            })}
          onSubmit={(values, { setSubmitting }) => {
            const { editedMembershipOption, ...rest } = values;
            const { first_period_price, ...editedMembershipOptionWithoutPrice } = editedMembershipOption;

            const updatedValues = {
              ...rest,
              editedMembershipOption: editedMembershipOptionWithoutPrice,
            };

            handleSaveMembershipOption(updatedValues);
            setSubmitting(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            isValid,
            dirty,
            setFieldValue,
            handleBlur,
            handleChange,
            handleSubmit
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid item xs={12}>
                <CardContent className="px-0">
                  <Grid container xs={12} className="p-0">
                    <TextField
                      className="min-w-260 mr-2"
                      name="frequency"
                      select
                      label={'Frequency'}
                      value={values.frequency}
                      variant="outlined"
                      onChange={(event) => handleFrequencyChange(event, setFieldValue, handleChange)}
                      onBlur={handleBlur}
                      inputProps={{ className: 'text-center' }}
                      error={touched.frequency && Boolean(errors.frequency)}
                      helperText={touched.frequency && errors.frequency}
                    >
                      {frequencyOptions.map((option, idx) => (
                        <MenuItem
                          key={`option-${idx}`}
                          value={option}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>

                    <NumberFormat
                      required
                      className="min-w-260"
                      prefix="$ "
                      fixedDecimalScale
                      decimalScale={2}
                      thousandSeparator
                      label={'First Period Price'}
                      variant="outlined"
                      name="editedMembershipOption.first_period_price"
                      value={values.editedMembershipOption.first_period_price}
                      onValueChange={({ floatValue }) => handlePriceChange('first_period_price', floatValue, setFieldValue, values)}
                      onBlur={handleBlur}
                      error={touched.editedMembershipOption?.first_period_price && Boolean(errors.editedMembershipOption?.first_period_price)}
                      helperText={touched.editedMembershipOption?.first_period_price && errors.editedMembershipOption?.first_period_price}
                      customInput={TextField}
                      inputProps={{ className: 'text-center' }}
                    />

                    <NumberFormat
                      required
                      className="min-w-260 mt-4 mr-2"
                      prefix="$ "
                      fixedDecimalScale
                      decimalScale={2}
                      thousandSeparator
                      label={'Recurring Price'}
                      variant="outlined"
                      name="editedMembershipOption.installment_price"
                      value={values.editedMembershipOption.installment_price}
                      onValueChange={({ floatValue }) => handlePriceChange('installment_price', floatValue, setFieldValue, values)}
                      onBlur={handleBlur}
                      error={touched.editedMembershipOption?.installment_price && Boolean(errors.editedMembershipOption?.installment_price)}
                      helperText={touched.editedMembershipOption?.installment_price && errors.editedMembershipOption?.installment_price}
                      customInput={TextField}
                      inputProps={{ className: 'text-center' }}
                    />

                    <TextField
                      required
                      className="min-w-260 mt-4"
                      name="editedMembershipOption.sproduct_label"
                      label={'Option Label'}
                      value={values.editedMembershipOption.sproduct_label}
                      variant="outlined"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ className: 'text-center' }}
                      error={touched.editedMembershipOption?.sproduct_label && Boolean(errors.editedMembershipOption?.sproduct_label)}
                      helperText={touched.editedMembershipOption?.sproduct_label && errors.editedMembershipOption?.sproduct_label}
                    />
                    <NumberFormat
                      required
                      allowNegative={false}
                      className="min-w-260 mt-4 mr-2"
                      label={'Sort Order'}
                      variant="outlined"
                      name="editedMembershipOption.ssort_order"
                      value={values.editedMembershipOption.ssort_order}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.editedMembershipOption?.ssort_order && Boolean(errors.editedMembershipOption?.ssort_order)}
                      helperText={touched.editedMembershipOption?.ssort_order && errors.editedMembershipOption?.ssort_order}
                      customInput={TextField}
                      inputProps={{ className: 'text-center' }}
                    />
                  </Grid>
                </CardContent>
                <DialogActions className="m-4">
                  <Button
                    className="text-muted"
                    onClick={() => setEditableMembership(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!(isValid && dirty)}
                    color="primary"
                    variant="contained"
                  >
                    Edit Option
                  </Button>
                </DialogActions>
              </Grid>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditMembershipModal;
